import { HubSelect } from "@/components/HubSelect";
import { useAuth } from "@/lib/auth";
import { Button } from "@bednbody/ui/components/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from "@bednbody/ui/components/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Field, FieldError, FieldGroup, FieldLabel } from "@bednbody/ui/components/field";
import { Input } from "@bednbody/ui/components/input";
import { useForm } from "@tanstack/react-form";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useHubsList } from "../../ops-dashboard/ops-dashboard.hooks";
import { ASSIGNABLE_STAFF_ROLES, type AssignableStaffRole, useCreateStaff } from "../roster.hooks";

interface AddEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Preselect the hub the roster is currently filtered to. */
  defaultHubId: string | null;
}

const roleLabel = (role: string) => role.replace(/_/g, " ");

/** Field staff must belong to a hub — dispatch and hub-scoped RLS key off hub_id. */
const isHubScopedRole = (role: AssignableStaffRole) =>
  role === "therapist" || role === "rider" || role === "hub_manager";

const staffSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  email: z.string().email("Invalid email address"),
  role: z.enum(ASSIGNABLE_STAFF_ROLES),
  hubId: z.string(),
  password: z.string(),
});

export function AddEmployeeModal({ isOpen, onClose, defaultHubId }: AddEmployeeModalProps) {
  const { data: hubs } = useHubsList();
  // Super Admin types the password and hands it over; no email is sent. HR and
  // Admin keep the emailed invite (the server enforces the same split).
  const setsPassword = useAuth().claims?.role === "superadmin";
  const createStaff = useCreateStaff();
  const [role, setRole] = useState<AssignableStaffRole>("therapist");
  const hubRequired = isHubScopedRole(role);

  const form = useForm({
    defaultValues: {
      fullName: "",
      email: "",
      role: "therapist" as AssignableStaffRole,
      hubId: defaultHubId ?? "",
      password: "",
    },
    validators: {
      onChange: staffSchema,
      onBlur: staffSchema,
      onSubmit: staffSchema,
    },
    onSubmit: async ({ value }) => {
      if (isHubScopedRole(value.role as AssignableStaffRole) && !value.hubId) {
        toast.error("Hub is required for this role");
        return;
      }
      if (setsPassword && value.password.length < 8) {
        toast.error("Password must be at least 8 characters");
        return;
      }

      try {
        await createStaff.mutateAsync({
          fullName: value.fullName.trim(),
          email: value.email.trim().toLowerCase(),
          role: value.role,
          hubId: value.hubId || null,
          ...(setsPassword ? { password: value.password } : {}),
        });
        if (setsPassword) {
          toast.success(`Account created for ${value.email.trim().toLowerCase()}`, {
            description:
              "They can sign in now with the password you set. Give it to them in person. Manager roles are asked to set up TOTP at first sign-in.",
          });
        } else {
          toast.success(`Invitation sent to ${value.email.trim().toLowerCase()}`, {
            description:
              "They set their own password from the emailed link. Manager roles are asked to set up TOTP at first sign-in.",
          });
        }
        form.reset();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to add employee.");
      }
    },
  });

  const handleClose = () => {
    createStaff.reset();
    form.reset();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Employee</DialogTitle>
          <DialogDescription>
            Create a new staff account with role and hub assignment.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="space-y-4"
        >
          <FieldGroup>
            <form.Field
              name="fullName"
              children={(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>Full name</FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={isInvalid}
                    />
                    {isInvalid ? <FieldError errors={field.state.meta.errors} /> : null}
                  </Field>
                );
              }}
            />

            <form.Field
              name="email"
              children={(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>Email</FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="email"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={isInvalid}
                    />
                    {isInvalid ? <FieldError errors={field.state.meta.errors} /> : null}
                  </Field>
                );
              }}
            />

            {setsPassword ? (
              <form.Field
                name="password"
                children={(field) => (
                  <Field>
                    <FieldLabel htmlFor={field.name}>Password</FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      maxLength={72}
                      required
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      At least 8 characters. No email is sent — give it to them yourself.
                    </p>
                  </Field>
                )}
              />
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <form.Field
                name="role"
                children={(field) => {
                  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>Role</FieldLabel>
                      <Combobox
                        items={[...ASSIGNABLE_STAFF_ROLES]}
                        value={field.state.value}
                        onValueChange={(val) => {
                          if (!val) return;
                          field.handleChange(val as AssignableStaffRole);
                          setRole(val as AssignableStaffRole);
                        }}
                        itemToStringLabel={roleLabel}
                      >
                        <ComboboxTrigger
                          render={
                            <Button
                              id={field.name}
                              variant="outline"
                              className="w-full justify-between font-normal capitalize"
                            />
                          }
                        >
                          <ComboboxValue placeholder="Select role…" />
                        </ComboboxTrigger>
                        <ComboboxContent>
                          <ComboboxInput placeholder="Search roles…" showTrigger={false} />
                          <ComboboxEmpty>No roles found.</ComboboxEmpty>
                          <ComboboxList>
                            {(role) => (
                              <ComboboxItem key={role} value={role} className="capitalize">
                                {roleLabel(role)}
                              </ComboboxItem>
                            )}
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                      {isInvalid ? <FieldError errors={field.state.meta.errors} /> : null}
                    </Field>
                  );
                }}
              />

              <form.Field
                name="hubId"
                children={(field) => {
                  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor="hub-select">
                        Hub{hubRequired ? "" : " (optional)"}
                      </FieldLabel>
                      <HubSelect
                        allLabel="No hub (office)"
                        placeholder={hubRequired ? "Pick a hub…" : "No hub (office)"}
                        className="w-full"
                        hubs={hubs ?? []}
                        id="hub-select"
                        variant="combobox"
                        value={field.state.value || null}
                        onValueChange={(hubId) => field.handleChange(hubId ?? "")}
                      />
                      {isInvalid ? <FieldError errors={field.state.meta.errors} /> : null}
                    </Field>
                  );
                }}
              />
            </div>
          </FieldGroup>

          <DialogFooter>
            <Button variant="outline" type="button" onClick={handleClose}>
              Close
            </Button>
            <Button type="submit" disabled={createStaff.isPending}>
              {createStaff.isPending ? "Adding…" : "Add Employee"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
